import { z } from 'zod'
import { BaseGatewayErrorDTO } from '@cplug/frontend-core/gateways'
import { ErrorCodes, isSuccessResponse, type BaseError } from '@cplug/frontend-core/models'
import { Result } from '@cplug/frontend-core/utils'
import type { TwoFactorBackupCodes } from '../models/TwoFactorBackupCodes'
import type { TwoFactorBackupCodesRemaining } from '../models/TwoFactorBackupCodesRemaining'
import type { TwoFactorSetupChallenge } from '../models/TwoFactorSetupChallenge'
import type { TwoFactorStatus } from '../models/TwoFactorStatus'
import type { TwoFactorTotpSetup } from '../models/TwoFactorTotpSetup'
import { mapAuthorizeWithTwoFactorCodeToDTO } from './DTOs/AuthorizeWithTwoFactorCodeDTO'
import { mapConfirmTwoFactorSetupToDTO } from './DTOs/ConfirmTwoFactorSetupDTO'
import { mapDisableTwoFactorToDTO } from './DTOs/DisableTwoFactorDTO'
import { mapRegenerateTwoFactorBackupCodesToDTO } from './DTOs/RegenerateTwoFactorBackupCodesDTO'
import { mapResendTwoFactorEmailSetupToDTO } from './DTOs/ResendTwoFactorEmailSetupDTO'
import {
  mapDTOToTwoFactorRevealedBackupCodes,
  TwoFactorRevealedBackupCodesDTO
} from './DTOs/TwoFactorRevealedBackupCodesDTO'
import {
  mapDTOToTwoFactorBackupCodes,
  TwoFactorBackupCodesDTO
} from './DTOs/TwoFactorBackupCodesDTO'
import {
  mapDTOToTwoFactorBackupCodesRemaining,
  TwoFactorBackupCodesRemainingDTO
} from './DTOs/TwoFactorBackupCodesRemainingDTO'
import {
  mapDTOToTwoFactorSetupChallenge,
  TwoFactorSetupChallengeDTO
} from './DTOs/TwoFactorSetupChallengeDTO'
import { mapDTOToTwoFactorStatus, TwoFactorStatusDTO } from './DTOs/TwoFactorStatusDTO'
import { mapDTOToTwoFactorTotpSetup, TwoFactorTotpSetupDTO } from './DTOs/TwoFactorTotpSetupDTO'
import { mapVerifyTwoFactorSetupToDTO } from './DTOs/VerifyTwoFactorSetupDTO'
import CPBaseGateway from '~/shared/gateways/CPBaseGateway'

const TwoFactorStatusWrapperDTO = z.object({ data: TwoFactorStatusDTO })
const TwoFactorTotpSetupWrapperDTO = z.object({ data: TwoFactorTotpSetupDTO })
const TwoFactorSetupChallengeWrapperDTO = z.object({ data: TwoFactorSetupChallengeDTO })
const TwoFactorBackupCodesWrapperDTO = z.object({ data: TwoFactorBackupCodesDTO })
const TwoFactorRevealedBackupCodesWrapperDTO = z.object({ data: TwoFactorRevealedBackupCodesDTO })
const TwoFactorBackupCodesRemainingWrapperDTO = z.object({ data: TwoFactorBackupCodesRemainingDTO })

export default class CPTwoFactorGateway extends CPBaseGateway {
  protected apiUrlDomain = 'two-factor'

  async getStatus (): Promise<Result<TwoFactorStatus>> {
    const response = await this.get(`${this.apiUrlDomain}/status`)

    return this.parseResponse(response, TwoFactorStatusWrapperDTO, dto =>
      mapDTOToTwoFactorStatus(dto.data)
    )
  }

  async setupTotp (): Promise<Result<TwoFactorTotpSetup>> {
    const response = await this.post(`${this.apiUrlDomain}/setup/totp`)

    return this.parseResponse(response, TwoFactorTotpSetupWrapperDTO, dto =>
      mapDTOToTwoFactorTotpSetup(dto.data)
    )
  }

  async setupEmail (): Promise<Result<TwoFactorSetupChallenge>> {
    const response = await this.post(`${this.apiUrlDomain}/setup/email`)

    return this.parseResponse(response, TwoFactorSetupChallengeWrapperDTO, dto =>
      mapDTOToTwoFactorSetupChallenge(dto.data)
    )
  }

  async resendSetupEmail (setupChallengeId: string): Promise<Result<undefined>> {
    const data = mapResendTwoFactorEmailSetupToDTO(setupChallengeId)
    const response = await this.post(`${this.apiUrlDomain}/setup/resend-email`, data)

    return this.parseEmptyResponse(response)
  }

  async verifySetup (setupChallengeId: string, code: string): Promise<Result<undefined>> {
    const data = mapVerifyTwoFactorSetupToDTO(setupChallengeId, code)
    const response = await this.post(`${this.apiUrlDomain}/setup/verify`, data)

    return this.parseEmptyResponse(response)
  }

  async confirmSetup (
    setupChallengeId: string,
    revokeOtherSessions?: boolean
  ): Promise<Result<TwoFactorBackupCodes>> {
    const data = mapConfirmTwoFactorSetupToDTO(setupChallengeId, revokeOtherSessions)
    const response = await this.post(`${this.apiUrlDomain}/setup/confirm`, data)

    return this.parseResponse(response, TwoFactorBackupCodesWrapperDTO, dto =>
      mapDTOToTwoFactorBackupCodes(dto.data)
    )
  }

  async acknowledgeSetup (): Promise<Result<undefined>> {
    const response = await this.post(`${this.apiUrlDomain}/setup/acknowledge`)

    return this.parseEmptyResponse(response)
  }

  async abandonSetup (): Promise<Result<undefined>> {
    const response = await this.post(`${this.apiUrlDomain}/setup/abandon`)

    return this.parseEmptyResponse(response)
  }

  async disable (password: string, revokeOtherSessions?: boolean): Promise<Result<undefined>> {
    const data = mapDisableTwoFactorToDTO(password, revokeOtherSessions)
    const response = await this.post(`${this.apiUrlDomain}/disable`, data)

    return this.parseEmptyResponse(response)
  }

  async regenerateBackupCodes (code?: string): Promise<Result<TwoFactorBackupCodes>> {
    const data = mapRegenerateTwoFactorBackupCodesToDTO(code)
    const response = await this.post(`${this.apiUrlDomain}/backup-codes/regenerate`, data)

    return this.parseResponse(response, TwoFactorBackupCodesWrapperDTO, dto =>
      mapDTOToTwoFactorBackupCodes(dto.data)
    )
  }

  async revealBackupCodes (code: string): Promise<Result<TwoFactorBackupCodes>> {
    const data = mapAuthorizeWithTwoFactorCodeToDTO(code)
    const response = await this.post(`${this.apiUrlDomain}/backup-codes/reveal`, data)

    return this.parseResponse(response, TwoFactorRevealedBackupCodesWrapperDTO, dto =>
      mapDTOToTwoFactorRevealedBackupCodes(dto.data)
    )
  }

  async sendRevealBackupCodesEmail (): Promise<Result<undefined>> {
    const response = await this.post(`${this.apiUrlDomain}/backup-codes/reveal/send-email`)

    return this.parseEmptyResponse(response)
  }

  async resendRevealBackupCodesEmail (): Promise<Result<undefined>> {
    const response = await this.post(`${this.apiUrlDomain}/backup-codes/reveal/resend-email`)

    return this.parseEmptyResponse(response)
  }

  async sendRegenerateBackupCodesEmail (): Promise<Result<undefined>> {
    const response = await this.post(`${this.apiUrlDomain}/backup-codes/regenerate/send-email`)

    return this.parseEmptyResponse(response)
  }

  async resendRegenerateBackupCodesEmail (): Promise<Result<undefined>> {
    const response = await this.post(`${this.apiUrlDomain}/backup-codes/regenerate/resend-email`)

    return this.parseEmptyResponse(response)
  }

  async getBackupCodesRemaining (): Promise<Result<TwoFactorBackupCodesRemaining>> {
    const response = await this.get(`${this.apiUrlDomain}/backup-codes/remaining`)

    return this.parseResponse(response, TwoFactorBackupCodesRemainingWrapperDTO, dto =>
      mapDTOToTwoFactorBackupCodesRemaining(dto.data)
    )
  }

  private parseEmptyResponse (response: Awaited<ReturnType<CPBaseGateway['post']>>): Result<undefined> {
    if (response === null) {
      return Result.ofError({ code: ErrorCodes.Unknown })
    }

    if (!isSuccessResponse(response)) {
      return Result.ofError(
        new BaseGatewayErrorDTO().deserialize(response.data) ?? <BaseError>{ code: ErrorCodes.Unknown }
      )
    }

    return Result.ofValue(undefined)
  }
}
